import { config as AtlasConfig } from '@sofarocean/atlas';
import { TimelineContext } from '@sofarocean/atlas/contexts/TimelineContext';
import { useWeatherInfo } from '@sofarocean/atlas/contexts/WeatherContext';
import { ConnectedSpotterLayer, MapClickLayer } from '@sofarocean/atlas/maps';
import { GM_Point } from '@sofarocean/atlas/types';
import { PointLayer, PointLayerProps } from '@sofarocean/mapbox-context';
import MapboxContext from '@sofarocean/mapbox-context/dist/contexts/MapboxContext';
import { addHours, isAfter, isBefore, roundToNearestHours } from 'date-fns';
import { filter, find, isEqual, last, map, maxBy } from 'lodash';
import mapboxgl, { ControlPosition, MapEventOf } from 'mapbox-gl';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useDeviceTrackHistory } from '@/api/device';
import { useHydrophoneDevices } from '@/api/device/useHydrophoneDevices';
import { useDeviceIdList } from '@/api/deviceIdList';
import { Device, useDevices } from '@/api/devices';
import { useScoutLatestData, useScoutsLatestData } from '@/api/scout';
import { useDisplayPreferences } from '@/api/userAccount';
import { scoutBlueIcon, scoutPurpleIcon } from '@/assets';
import spotterImageBlue from '@/assets/map/spotter-blue.png';
import spotterImagePurple from '@/assets/map/spotter-purple.png';
import spotterImageYellow from '@/assets/map/spotter-yellow.png';
import scoutIcon from '@/assets/scout-card.svg';
import {
    MapBaseLayerToggle,
    MapControlsStack,
    MapTimestamp,
    ModelSelector,
    MyLocationButton,
    SpotterTypeSelector,
    ToggleLiveTime,
    WeatherLayerSelector,
} from '@/components/map/LayerControls';
import { MapDetectionChips } from '@/components/map/MapDetectionChips';
import { MapTimeframeSelector } from '@/components/map/MapTimeframeSelector';
import { SafeMap } from '@/components/map/SafeMap';
import { useSpotterListFilterFns } from '@/components/map/SpotterList';
import { InspectorPopup } from '@/components/map/inspectors/InspectorPopup';
import { GeofenceDisplayLayer } from '@/components/map/layers/GeofenceDisplayLayer';
import { Graticules } from '@/components/map/layers/Graticules';
import { MyLocationLayer } from '@/components/map/layers/MyLocationLayer';
import { MySpotterLayer } from '@/components/map/layers/MySpotterLayer';
import { ScoutWaypointsLayer } from '@/components/map/layers/ScoutWaypointsLayer';
import { SplHalos } from '@/components/map/layers/SplHalos';
import { INACTIVE_SPOTTER_MS } from '@/config';
import { useMapUiContext } from '@/context/MapUiContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useDateRangeForSample } from '@/hooks/useDateRangeForSample';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useGeoLocation } from '@/hooks/useGeoLocation';
import { useNow } from '@/hooks/useNow';
import { useSpotterHash } from '@/hooks/useSpotterHash';
import { MapType } from '@/pages/Map';
import { DeviceMapLocation, calculateDeviceLocation, isScoutDevice, lngLat } from '@/utils';

export const DEFAULT_GEOLOCATION = { latitude: 37.7749, longitude: -122.4194 } as const;

export const SPOTTER_ICONS = {
    mine: { label: 'My Spotters', color: 'yellow', img: spotterImageYellow },
    grid: { label: 'Sofar Spotters', color: 'blue', img: spotterImageBlue },
    shared: { label: 'Public Spotters', color: 'purple', img: spotterImagePurple },
} as const;

export const SCOUT_ICONS = {
    mine: scoutIcon,
    grid: scoutBlueIcon,
    shared: scoutPurpleIcon,
} as const;

export type SpotterMapProps = {
    /**
     * Called by MapSpotterList to inform containiner when a spotter
     * becomes hovered (or undefined on leaving hover)
     */
    onSpotterHover?: (spotterId: string | undefined) => void;
    /**
     * Spotter that should be given hover treatment
     */
    hoveredSpotterId?: string | undefined;
    mapType: MapType;
    children?: ReactNode;
};

export type EnrichedMapDevice = Device & {
    heading?: number;
    location?: DeviceMapLocation;
};

export const SpotterMap = ({ hoveredSpotterId, onSpotterHover, mapType, children }: SpotterMapProps) => {
    const {
        weatherLayers,
        spotterLayers,
        weatherModel,
        showMyLocation,
        mapInspector,
        setMapInspector,
        spotterListOptions,
    } = useMapUiContext();
    const locationsForSelectedTimestamp = mapType === 'acoustics';
    const { selectedTimestamp, setSelectedTimestamp } = useTimelineNavigationContext();
    const { spotterId } = useSpotterHash();
    const weatherMapFlag = useFeatureFlag('weatherMap');
    const spotterListFilterFns = useSpotterListFilterFns();
    const spotterFilterFn = spotterListFilterFns[locationsForSelectedTimestamp ? 'active' : spotterListOptions.filter];

    const { data: deviceIdItems } = useDeviceIdList();
    const { data: devicesResult } = useDevices({
        devices: deviceIdItems?.data?.devices ?? [],
    });

    const devices = useMemo(() => {
        if (!devicesResult) {
            return [];
        }
        return devicesResult;
    }, [devicesResult]);

    const hydrophoneDevices = useHydrophoneDevices(devices, { enabled: mapType === 'acoustics' });
    const mapViewableDevices = mapType === 'acoustics' ? hydrophoneDevices : devices;

    const mySpotterIds = useMemo(
        () => (!spotterLayers.mySpotters ? [] : mapViewableDevices?.map((d) => d.spotterId)),
        [mapViewableDevices, spotterLayers.mySpotters]
    );

    // Find the selected device for geofence display
    const selectedDevice = useMemo(
        () => (spotterId ? find(mapViewableDevices, { spotterId }) : undefined),
        [mapViewableDevices, spotterId]
    );
    const showGeofence = spotterId && !isScoutDevice(selectedDevice?.hullType);

    const filteredDevices = useMemo(
        () => filter(mapViewableDevices, (d) => spotterFilterFn(d) || d === selectedDevice),
        [mapViewableDevices, selectedDevice, spotterFilterFn]
    );

    const filteredDeviceIds = useMemo(() => map(filteredDevices, 'spotterId'), [filteredDevices]);

    const { data: trackHistories } = useDeviceTrackHistory({
        spotterIds: filteredDeviceIds,
        dateRange: useDateRangeForSample(selectedTimestamp),
        config: { enabled: locationsForSelectedTimestamp },
    });

    const { data: scoutsLatestData } = useScoutsLatestData({
        spotterIdentities: filteredDevices,
        config: { enabled: spotterLayers.mySpotters || !!spotterId },
    });

    const enrichedDevices = useMemo(
        () =>
            (filteredDevices ?? []).map((device, i) => {
                const trackHistory = trackHistories?.[i];
                const scoutLatestData = scoutsLatestData?.[device.spotterId];
                const scoutHeading = scoutLatestData?.position?.heading_deg;
                const location = calculateDeviceLocation({
                    spotterTrack: trackHistory ?? device.track,
                    scoutLocation: scoutLatestData,
                    selectedTimestamp: locationsForSelectedTimestamp ? selectedTimestamp : undefined,
                });

                return {
                    ...device,
                    ...(scoutHeading != null && { heading: scoutHeading }),
                    ...(location && { location }),
                };
            }),
        [filteredDevices, locationsForSelectedTimestamp, scoutsLatestData, selectedTimestamp, trackHistories]
    );

    useEffect(() => {
        // Close the map inspector if we navigated to a different spotter, or update the inspector position if our spotter moved
        if (spotterId && mapInspector?.type === 'spotter' && mapInspector.spotterFamily === 'mine') {
            if (mapInspector.spotterId !== spotterId) {
                setMapInspector(undefined);
            }
            if (locationsForSelectedTimestamp && mapInspector.spotterId === spotterId) {
                const newLocation = find(enrichedDevices, { spotterId })?.location;
                const newPosition = newLocation && lngLat(newLocation);
                if (newPosition && !isEqual(newPosition, mapInspector.position)) {
                    setMapInspector({
                        type: 'spotter',
                        spotterFamily: 'mine',
                        spotterId: spotterId as string,
                        position: newPosition,
                    });
                }
            }
        }
    }, [spotterId, mapInspector, setMapInspector, locationsForSelectedTimestamp, enrichedDevices]);

    useEffect(() => {
        // if we're not respecting the selected timestamp, clear the cursor so it doesn't unintentionally
        // influence things.
        if (!locationsForSelectedTimestamp) {
            setSelectedTimestamp(null);
        }
    }, [locationsForSelectedTimestamp, setSelectedTimestamp]);

    const onClickMap = useCallback(
        (position: GM_Point) => {
            if (mapInspector) {
                setMapInspector(undefined);
            } else if (weatherMapFlag && weatherModel) {
                setMapInspector({ type: 'weather', position });
            }
        },
        [mapInspector, setMapInspector, weatherMapFlag, weatherModel]
    );

    return (
        <SafeMap
            mapType="main"
            showWeather={weatherMapFlag && !!weatherModel}
            allowMapZoomOnScroll
            weatherLayers={weatherLayers}
        >
            {children}
            {weatherMapFlag && !!weatherModel && <WeatherUpdater />}
            <PanZoomEffects
                selectedSpotterId={spotterId}
                allDevicesOnMap={mapViewableDevices}
                updateSpottersInViewport={mapType === 'acoustics'}
            />
            <InspectorPopup historicalData={mapType === 'acoustics'} />
            <MapClickLayer onClickMap={onClickMap} />
            <ScaleControl maxWidth={160} />
            {/* attachment points for spotter layers so we can turn them on an off without breaking beforeLayer */}
            <DummyLayer id="mySpottersAnchor" />
            <DummyLayer id="geofenceAnchor" beforeLayer="mySpottersAnchor" />
            <DummyLayer id="publicAnchor" beforeLayer="geofenceAnchor" />
            <DummyLayer id="gridAnchor" beforeLayer="publicAnchor" />
            {spotterLayers.gridSpotters && (
                <ConnectedSpotterLayer
                    beforeLayer="gridAnchor"
                    spotterColor={SPOTTER_ICONS.grid.color}
                    spotterDotWhenSmall
                    filter={{ sofarGridSpotters: true, excludeSpotterIds: mySpotterIds, maxAgeMs: INACTIVE_SPOTTER_MS }}
                    onSpotterClick={(spotterId, { position }) =>
                        setMapInspector({
                            type: 'spotter',
                            spotterFamily: 'grid',
                            spotterId: spotterId as string,
                            position: position,
                        })
                    }
                />
            )}
            {spotterLayers.sharedSpotters && (
                <ConnectedSpotterLayer
                    beforeLayer="publicAnchor"
                    spotterColor={SPOTTER_ICONS.shared.color}
                    spotterDotWhenSmall
                    filter={{ publicSpotters: true, excludeSpotterIds: mySpotterIds, maxAgeMs: INACTIVE_SPOTTER_MS }}
                    onSpotterClick={(spotterId, { position }) =>
                        setMapInspector({
                            type: 'spotter',
                            spotterFamily: 'shared',
                            spotterId: spotterId as string,
                            position: position,
                        })
                    }
                />
            )}
            {spotterLayers.mySpotters && enrichedDevices && (
                <>
                    <MySpotterLayer
                        layerId="my-spotters"
                        beforeLayer="mySpottersAnchor"
                        eventHandlerPriority={2}
                        devices={enrichedDevices}
                        selectedSpotterId={spotterId}
                        hoveredSpotterId={hoveredSpotterId}
                        onSpotterHover={onSpotterHover}
                    />
                    {mapType === 'acoustics' && (
                        <>
                            <SplHalos devices={enrichedDevices} beforeLayer="my-spotters" />
                            <MapDetectionChips devices={enrichedDevices} />
                        </>
                    )}
                </>
            )}
            {showGeofence && (
                <GeofenceDisplayLayer
                    layerId={`geofence-${spotterId}`}
                    device={selectedDevice}
                    beforeLayer="geofenceAnchor"
                />
            )}
            {spotterId && <ScoutWaypointsLayer beforeLayer="geofenceAnchor" spotterId={spotterId} />}
            <MapControlsStack
                hideBelowSizePx={128}
                sx={{
                    gap: '4px',
                    right: 4,
                    bottom: 4,
                    flexDirection: 'column',
                    alignItems: 'end',
                }}
            >
                <MapBaseLayerToggle noSatellite={!!weatherModel} />
                {mapType === 'acoustics' && <MapTimeframeSelector />}
            </MapControlsStack>
            {showMyLocation && <MyLocationLayer />}
            <MapControlsStack
                hideBelowSizePx={96}
                sx={{
                    right: 4,
                    top: 4,
                }}
            >
                <MyLocationButton />
                {mapType !== 'acoustics' && (
                    <>
                        <SpotterTypeSelector />
                        {weatherModel && <WeatherLayerSelector />}
                        <ModelSelector />
                    </>
                )}
            </MapControlsStack>
            {mapType === 'acoustics' && (
                <MapControlsStack
                    sx={{
                        left: 8,
                        top: 8,
                    }}
                >
                    <ToggleLiveTime />
                    <MapTimestamp />
                </MapControlsStack>
            )}
            <Graticules />
        </SafeMap>
    );
};

const PanZoomEffects = ({
    selectedSpotterId,
    allDevicesOnMap,
    updateSpottersInViewport,
}: {
    selectedSpotterId?: string;
    allDevicesOnMap?: Device[];
    updateSpottersInViewport: boolean;
}) => {
    const { map } = useContext(MapboxContext);
    const { setCameraOptions, cameraOptions, showMyLocation, setSpotterIdsInViewport } = useMapUiContext();
    const { coords } = useGeoLocation();
    const [rendered, setRendered] = useState(false);
    const hasCameraOptions = !!cameraOptions;
    const [initializedLocation, setInitializedLocation] = useState(false);
    const prevShowMyLocation = useRef(showMyLocation);

    const selectedDevice = useMemo(
        () => (selectedSpotterId ? find(allDevicesOnMap, { spotterId: selectedSpotterId }) : undefined),
        [allDevicesOnMap, selectedSpotterId]
    );

    const isScout = selectedDevice ? isScoutDevice(selectedDevice.hullType) : false;
    const { data: scoutLatestData } = useScoutLatestData({
        spotterId: selectedSpotterId,
        config: { enabled: isScout },
    });

    /**
     * Effect: Set the initial location:
     * - If there's a saved context location, use that.
     * - If there's a selected or most recently updated spotter, use that.
     * - If there are no spotters, we let the mapbox style's initial location stand, which prevents jumping around too much during
     * load, and if the user really has no spotters left, leaves them on a nice world-wide view until they register a device.
     */
    useEffect(() => {
        // Set initial map center
        if (!map || initializedLocation) {
            return;
        }

        if (cameraOptions) {
            map.jumpTo(cameraOptions);
            setInitializedLocation(true);
            return;
        }

        if (!allDevicesOnMap) {
            return;
        }

        const device = !selectedSpotterId ? maxBy(allDevicesOnMap, 'timeLastUpdatedUTC') : selectedDevice;
        if (!device) {
            return;
        }

        // For scouts, wait for the latest scout data before flying so we don't lock in on a stale track[0].
        if (isScout && scoutLatestData === undefined) {
            return;
        }

        const location = calculateDeviceLocation({ spotterTrack: device.track, scoutLocation: scoutLatestData });
        if (location) {
            const cameraOptions = { center: [location.longitude, location.latitude] as [number, number], zoom: 6 };
            map.flyTo(cameraOptions);
            setCameraOptions(cameraOptions);
            setInitializedLocation(true);
        }
    }, [
        allDevicesOnMap,
        cameraOptions,
        initializedLocation,
        isScout,
        map,
        scoutLatestData,
        selectedDevice,
        selectedSpotterId,
        setCameraOptions,
    ]);

    /**
     * Effect: Fly to user location when showMyLocation is toggled on
     * but not every time the coords are updated while showMyLocation is ON
     */
    useEffect(() => {
        if (!map) return;

        const wasOff = !prevShowMyLocation.current;

        // If turning off, update ref and exit
        if (!showMyLocation) {
            prevShowMyLocation.current = false;
            return;
        }

        // If turning on (wasOff: true) and we have coords, fly
        if (wasOff && coords) {
            const newCameraOptions = {
                center: [coords.longitude, coords.latitude] as [number, number],
                zoom: Math.max(8, map.getZoom()),
            };
            map.flyTo(newCameraOptions);
            setCameraOptions(newCameraOptions);
            prevShowMyLocation.current = true; // Only mark as "on" after flying
        }
        // If turning on but no coords yet, DON'T update ref - wait for coords
        // When coords arrive, effect re-runs, wasOff is still true, and we fly
    }, [showMyLocation, coords, map, setCameraOptions]);

    const updateViewportSpotters = useCallback(() => {
        if (!map || !updateSpottersInViewport || !allDevicesOnMap) {
            return;
        }

        const viewport = map.getBounds();
        if (!viewport) {
            console.warn('Unable to determine map bounds for Spotter visibility');
            return;
        }

        const spotterIds = allDevicesOnMap
            ?.filter(({ track }) => {
                if (!track?.length) {
                    return false;
                }

                return viewport.contains(lngLat(track[0]));
            })
            .map(({ spotterId }) => spotterId);

        setSpotterIdsInViewport(spotterIds);
    }, [map, updateSpottersInViewport, allDevicesOnMap, setSpotterIdsInViewport]);

    /**
     * Effect: If any of the dependencies of updateViewportSpotters have changed, call it (e.g. update the viewport spotters if the list of candidate spotters has changed)
     */
    useEffect(() => {
        updateViewportSpotters();
    }, [updateViewportSpotters]);

    /**
     * Effect: Listen for manual pans and zooms, and save them in the MapUIContext for next map view, and update viewport spotters.
     */
    useEffect(() => {
        if (!map) {
            return;
        }

        const onPanZoom = (e: MapEventOf<'moveend'> | MapEventOf<'zoomend'>) => {
            if (hasCameraOptions || 'originalEvent' in e) {
                // if this move was programatic (no `originalEvent`), and we've never before moved the viewport manually,
                // don't save the location; it's probably just the map's default location and we don't want that to outweigh
                // our other initial location logic (based on your spotters)
                setCameraOptions({ center: map.getCenter(), zoom: map.getZoom() });
            }
            updateViewportSpotters();
        };

        map.on('zoomend', onPanZoom);
        map.on('moveend', onPanZoom);

        return () => {
            map.off('zoomend', onPanZoom);
            map.off('moveend', onPanZoom);
        };
    }, [hasCameraOptions, map, setCameraOptions, updateViewportSpotters]);

    /**
     * Effect: workaround, for some reason the map doesn't initially draw _quite_ all the way to the bottom of its
     * container, but if we fake a window resize after the first time the map renders, it fixes it.
     */
    useEffect(() => {
        if (!map) {
            return;
        }

        const onRender = () => {
            if (!rendered) {
                window.dispatchEvent(new Event('resize'));
                setRendered(true);
            }
        };

        map.on('render', onRender);

        return () => {
            map.off('render', onRender);
        };
    }, [map, rendered]);

    return <></>;
};

const WEATHER_NOW_REFRESH_SEC = 10;

/**
 * This is a utility component with no DOM output, just so we can conditionally include it in the map and avoid calling
 * useWeatherInfo when weather is off.
 */
const WeatherUpdater = () => {
    const { setCurrentSimulatorTime, currentSimulatorTime } = useContext(TimelineContext);
    const { weatherVariableMetadata } = useWeatherInfo({ autoupdate: true });
    const now = useNow(WEATHER_NOW_REFRESH_SEC);

    const nextOutputTime = useMemo(() => {
        const metadata = find(weatherVariableMetadata, {
            modelID: AtlasConfig.weatherVariables.combinedWaves.modelID,
            variableID: 'significantWaveHeight',
        });

        if (!metadata?.enumeratedOutputTimes?.length) {
            return null;
        }

        for (const outputTimeStr of metadata.enumeratedOutputTimes) {
            const outputTime = new Date(outputTimeStr);
            if (isAfter(outputTime, now)) {
                return outputTime;
            }
        }
        return new Date(last(metadata.enumeratedOutputTimes)!);
    }, [weatherVariableMetadata, now]);

    useEffect(() => {
        // if we don't know about output times, it's probably because we haven't initialized weather yet.
        // set it a little into the future in the hope that we hit the most upcoming forecast so we don't
        // have to refresh once the output times arrive
        const targetForecastTime = nextOutputTime ?? roundToNearestHours(addHours(now, 3), { roundingMethod: 'floor' });
        if (!currentSimulatorTime || isBefore(currentSimulatorTime, targetForecastTime)) {
            setCurrentSimulatorTime(targetForecastTime);
        }
    }, [currentSimulatorTime, nextOutputTime, now, setCurrentSimulatorTime]);

    return <></>;
};

export const ScaleControl = ({ maxWidth, position }: { maxWidth?: number; position?: ControlPosition }) => {
    const { data: displayPreferences } = useDisplayPreferences();
    const { map } = useContext(MapboxContext);
    const [scale, setScale] = useState<mapboxgl.ScaleControl>();

    useEffect(() => {
        if (map && !scale) {
            const newScale = new mapboxgl.ScaleControl({
                maxWidth,
                unit: 'imperial',
            });
            setScale(newScale);
            map.addControl(newScale, position);
        }
        return () => {
            if (scale) {
                try {
                    map?.removeControl(scale);
                } catch {
                    // ignore, scale's removeControl handler throws if the container has already been unmounted
                }
                setScale(undefined);
            }
        };
    }, [map, scale, maxWidth, position]);

    useEffect(() => {
        if (!scale) return;

        scale.setUnit(displayPreferences?.measuringDistance === 'km' ? 'metric' : 'imperial');
    }, [displayPreferences?.measuringDistance, scale]);

    return <></>;
};

/**
 * Just an attachment point so we can order layers that may be turned on or off, since having beforeLayer point to a nonexistent layer
 * breaks the after layer permanently.
 */
const NO_POINTS: PointLayerProps['points'] = [];
const DUMMY_STYLE = { paint: {}, layout: {} };

const DummyLayer = (props: { id: string; beforeLayer?: string }) => (
    <PointLayer {...props} style={DUMMY_STYLE} points={NO_POINTS} type="circle" />
);
